import React, { useCallback, useState } from "react";

import {
  AstridDaoStoreState,
  Decimal18,
  ERC20TokenConversion,
  SupportedCollaterals
} from "@astrid-dao/lib-base";
import { Text, Flex, Button, Image, Box } from "theme-ui";

import useTokenBalance from "../../../hooks/useTokenBalance";
import { TOKEN_PRECISION } from "../../constants";
import { iconCurrencyMap } from "../../currencyIcons";
import { ethersTransactionOverridesProxy } from "../../gasEstimator";
import { useAstridDaoSelector } from "../../hooks/useAstridDaoSelector";
import { useTokenPrices } from "../../hooks/useTokenPrices";
import { usdCurrencyFormatter } from "../../rpc-contract-service/utils/utils";
import { BASE_COLLAT } from "../../strings";
import { useTheme } from "../../theme";
import { getAmountToDisplay } from "../../utils/digits";
import { Icon } from "../Icon";
import { Transaction } from "../Transaction";
import { EditableRow, StaticRow } from "../Vault/Editor-v2";
import { useConvertWastrView } from "./context/ConvertWastrViewContext";
import { validateERC20TokenConversionChange } from "./validation/validateERC20TokenConversionChange";

const selector = (state: AstridDaoStoreState) => {
  const { accountBalance, wrappedTokenAllowance, wrappedTokenBalance } = state;
  return {
    accountBalance,
    wrappedTokenAllowance,
    wrappedTokenBalance
  };
};

const GAS_ROOM = 0.5;
const GAS_ROOM_DECIMAL18 = Decimal18.from(GAS_ROOM);
const TRANSACTION_ID = "deposit-collateral";

const EMPTY_CONVERSION_STATE = new ERC20TokenConversion(Decimal18.ZERO, Decimal18.ZERO);

type DepositingProps = {
  onCancel: VoidFunction;
  onSubmit: VoidFunction;
};

export const Depositing: React.FC<DepositingProps> = ({ onCancel, onSubmit }) => {
  const { theme } = useTheme();
  const tokenPricePerCollateral = useTokenPrices();
  const { dispatch, data, depositAstr } = useConvertWastrView();
  const editingState = useState<string>();
  const baseTokenPrice = Number(tokenPricePerCollateral?.get("WASTR")?.toString());
  const { accountBalance } = useAstridDaoSelector(selector);
  const wrappedTokenBalance = useTokenBalance("WASTR");
  const [baseToken, setBaseToken] = useState<Decimal18>(Decimal18.ZERO);
  const [, setWrappedToken] = useState<Decimal18>(Decimal18.ZERO);
  const maxBaseToken = accountBalance.gt(GAS_ROOM_DECIMAL18)
    ? accountBalance.sub(GAS_ROOM_DECIMAL18)
    : Decimal18.ZERO;
  const baseTokenMaxedOut = baseToken.gt(maxBaseToken);
  const baseTokenValue = baseTokenPrice * Number(baseToken.toString());
  const formattedBaseTokenValue = usdCurrencyFormatter.format(baseTokenValue);

  const reset = useCallback(() => {
    setBaseToken(Decimal18.ZERO);
    setWrappedToken(Decimal18.ZERO);
  }, []);

  const [isValid, description] = validateERC20TokenConversionChange(
    "DEPOSITING",
    EMPTY_CONVERSION_STATE,
    baseToken,
    { accountBalance, wrappedTokenBalance: data?.wastrBalance ?? Decimal18.ZERO }
  );

  const handleSwitchClick = useCallback(() => {
    dispatch({ type: "startWithdrawing" });
  }, [dispatch]);

  const rawDeposit = baseToken.gt(GAS_ROOM_DECIMAL18)
    ? baseToken.sub(GAS_ROOM_DECIMAL18)
    : baseToken;

  const [depositAmount] = getAmountToDisplay(rawDeposit);
  const depositAmountValue = Number(rawDeposit.toString() ?? 0) * baseTokenPrice;
  const formattedDepositAmountValue = usdCurrencyFormatter.format(depositAmountValue);

  const hasApproved = true;

  return (
    <Flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        width: "100%"
      }}
    >
      <Flex
        sx={{
          flexDirection: ["column", "column", "row"],
          justifyContent: "space-between",
          alignItems: ["flex-start", "flex-start", "center"],
          width: "100%"
        }}
      >
        <Text variant="text.msTitle16" sx={{ color: "B100" }}>
          FROM
        </Text>
        <Text variant="text.regularBodySmall" sx={{ color: "B60" }}>
          Balance: {accountBalance.prettify(TOKEN_PRECISION)} ASTR
        </Text>
      </Flex>

      <Box
        sx={{
          width: "100%",
          height: ["135px", "135px", "80px"],
          p: "0px",
          mt: 4
        }}
      >
        <EditableRow
          label=""
          inputId="convert-from"
          amount={baseToken.prettify(TOKEN_PRECISION)}
          amountValue={formattedBaseTokenValue}
          maxAmount={maxBaseToken.toString()}
          maxedOut={baseTokenMaxedOut}
          editingState={editingState}
          unit={BASE_COLLAT}
          unitIcon={
            <Image
              loading="lazy"
              width="24"
              height="24"
              alt={`${BASE_COLLAT} Logo`}
              src={iconCurrencyMap[BASE_COLLAT as SupportedCollaterals]}
              sx={{ minWidth: "24px" }}
            />
          }
          editedAmount={baseToken.toString(TOKEN_PRECISION)}
          setEditedAmount={(amount: string) => setBaseToken(Decimal18.from(amount))}
        />
      </Box>

      {/* <Tooltip message="Switch to Withdraw" placement="bottom"> */}
      <Button
        variant="icon"
        onClick={handleSwitchClick}
        sx={{
          width: "68px",
          height: "68px",
          boxShadow: "0px 2px 15px rgba(0, 0, 0, 0.15)",
          borderRadius: "16px",
          mt: 8
        }}
      >
        <Icon name="repeat" size="xl" color={theme.colors.B100} />
      </Button>
      {/* </Tooltip> */}

      <Flex
        sx={{
          flexDirection: ["column", "column", "row"],
          justifyContent: "space-between",
          alignItems: ["flex-start", "flex-start", "center"],
          width: "100%",
          mt: 8
        }}
      >
        <Text variant="text.msTitle16" sx={{ color: "B100" }}>
          TO
        </Text>
        <Text variant="text.regularBodySmall" sx={{ color: "B60" }}>
          Balance: {wrappedTokenBalance.shorten()} WASTR
        </Text>
      </Flex>

      <Box
        sx={{
          height: ["135px", "135px", "80px"],
          width: "100%",
          p: "0px",
          mt: 4
        }}
      >
        <StaticRow
          label=""
          inputId="convert-to"
          amount={depositAmount}
          amountValue={formattedDepositAmountValue}
          unit={"WASTR"}
          unitIcon={
            <Image
              loading="lazy"
              width="24"
              height="24"
              alt={"WASTR Logo"}
              src={iconCurrencyMap["WASTR" as SupportedCollaterals]}
            />
          }
          sx={{
            bg: "LG60",
            border: "1px solid G40",
            borderRadius: "20px",
            p: 6
          }}
        />
      </Box>

      <Flex
        sx={{
          flexDirection: "row",
          width: "100%",
          justifyContent: "center",
          mt: 14,
          alignItems: "center"
        }}
      >
        {!hasApproved && (
          <Box sx={{ flex: "1 1 0px" }}>
            <Button
              sx={{
                width: "100%",
                height: "40px"
              }}
              onClick={onCancel}
            >
              <Flex
                sx={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  border: "1.5px solid #FFFFFF",
                  mr: "8px",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px"
                }}
              >
                1
              </Flex>
              Approve
            </Button>
          </Box>
        )}

        {!hasApproved && (
          <div
            style={{
              height: "1px",
              width: "64px",
              background: "linear-gradient(262.97deg, #0BB7B5 59.64%, #FFD951 100.24%)"
            }}
          />
        )}

        <Box sx={{ flex: "1 1 0px" }}>
          <Transaction
            id={TRANSACTION_ID}
            send={overrides => {
              return depositAstr(baseToken, {
                ...overrides,
                gasPrice: ethersTransactionOverridesProxy.gasPrice
              } as any).finally(() => {
                onSubmit();
                reset();
              });
            }}
            showFailure="asTooltip"
            tooltipPlacement="bottom"
            onSuccess={async () => {
              const refetchTokenBalanceFlag =
                window.localStorage.getItem("refetchTokenBalanceFlag") || 0;
              window.localStorage.setItem(
                "refetchTokenBalanceFlag",
                String(Number(refetchTokenBalanceFlag) + 1)
              );
            }}
          >
            <Button
              sx={{
                width: "100%",
                height: "40px"
              }}
              disabled={!isValid}
            >
              {!hasApproved && (
                <Flex
                  sx={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    border: "1.5px solid #FFFFFF",
                    mr: "8px",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px"
                  }}
                >
                  2
                </Flex>
              )}
              Convert
            </Button>
          </Transaction>
        </Box>
      </Flex>
    </Flex>
  );
};
